/**
 * Version: V0.0.3.2
 * Created: 2026-06-12
 * Updated: 2026-06-13
 * Description: Reusable multi-column footer component matching F:\\Platforms\\Projects\\DentalFlow\\dentiefy.com\\frontend layout.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import Logo from '../common/Logo';

export default function Footer() {
  const { t, language, setLanguage } = useLanguage();

  const [companyInfo, setCompanyInfo] = React.useState(null);

  React.useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001';
    fetch(`${API_BASE}/api/contacts`)
      .then(res => res.json())
      .then(data => {
        if (data) setCompanyInfo(data);
      })
      .catch(err => console.error('Error fetching footer details:', err));
  }, []);

  const getLocalizedDescription = () => {
    if (!companyInfo) return t('footer_tagline');
    if (language === 'fr') return companyInfo.description_fr || companyInfo.description;
    if (language === 'ar') return companyInfo.description_ar || companyInfo.description;
    return companyInfo.description;
  };

  const currentYear = new Date().getFullYear();

  const getCopyrightText = () => {
    let brand = 'Dentiefy';
    if (language === 'ar') brand = 'دانتي فاي';

    const base = `© 2025/${currentYear} ${brand}.`;
    if (language === 'fr') return `${base} Tous droits réservés.`;
    if (language === 'ar') return `© 2025/${currentYear} ${brand}. جميع الحقوق محفوظة.`;
    return `${base} All rights reserved.`;
  };

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'ar', name: 'العربية' }
  ];

  return (
    <footer className="bg-slate-950 text-slate-200 dark:bg-black/25 w-full transition-colors duration-300">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center md:text-start">

          {/* Logo & Tagline */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center justify-center md:justify-start">
              <Link to="/" onClick={handleLinkClick}>
                <Logo color="text-violet-300" />
              </Link>
            </div>
            <p className="mt-4 text-sm font-sans leading-relaxed text-slate-300">
              {getLocalizedDescription()}
            </p>
            <div className="mt-6 flex justify-center md:justify-start space-x-4 rtl:space-x-reverse">
              {(() => {
                const socials = companyInfo?.social_links || {};
                const fb = socials.facebook || 'https://web.facebook.com/dentiefy';
                const tw = socials.twitter || 'https://x.com/dentiefy';
                const li = socials.linkedin || 'https://linkedin.com/company/dentiefy';
                const ig = socials.instagram || 'https://www.instagram.com/dentiefy';

                return (
                  <>
                    <a href={fb} target="_blank" rel="noopener noreferrer" aria-label="Follow Dentiefy on Facebook" className="text-slate-300 hover:text-white transition-colors duration-250"><Facebook className="w-5 h-5" /></a>
                    <a href={tw} target="_blank" rel="noopener noreferrer" aria-label="Follow Dentiefy on Twitter" className="text-slate-300 hover:text-white transition-colors duration-250"><Twitter className="w-5 h-5" /></a>
                    <a href={li} target="_blank" rel="noopener noreferrer" aria-label="Follow Dentiefy on LinkedIn" className="text-slate-300 hover:text-white transition-colors duration-250"><Linkedin className="w-5 h-5" /></a>
                    <a href={ig} target="_blank" rel="noopener noreferrer" aria-label="Follow Dentiefy on Instagram" className="text-slate-300 hover:text-white transition-colors duration-250"><Instagram className="w-5 h-5" /></a>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Product Links */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-white tracking-wider uppercase text-sm font-sans">{t('footer_product')}</h3>
            <ul className="mt-4 space-y-2 text-sm font-sans text-slate-300">
              <li><Link to="/features" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_features')}</Link></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_pricing')}</Link></li>
              <li><Link to="/demo-request" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_demo') || 'Request a Demo'}</Link></li>
              <li><Link to="/downloads" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_downloads')}</Link></li>
              <li><Link to="/partners" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_partners')}</Link></li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-white tracking-wider uppercase text-sm font-sans">{t('footer_company')}</h3>
            <ul className="mt-4 space-y-2 text-sm font-sans text-slate-300">
              <li><Link to="/about-us" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_about')}</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_blog')}</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_contact')}</Link></li>
              <li><Link to="/careers" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_careers')}</Link></li>
              <li><Link to="/support" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_support')}</Link></li>
              <li><a href={
                (() => {
                  const host = window.location.hostname;
                  const protocol = window.location.protocol;
                  const port = window.location.port;
                  if (host.includes('localhost')) {
                    return `${protocol}//status.localhost${port ? `:${port}` : ''}`;
                  }
                  const devDomain = process.env.REACT_APP_MAIN_DOMAIN_DEV || 'dentiefy.test';
                  const prodDomain = process.env.REACT_APP_MAIN_DOMAIN_PROD || 'dentiefy.com';
                  if (host.includes(devDomain)) {
                    return `${protocol}//status.${devDomain}${port ? `:${port}` : ''}`;
                  }
                  return `https://status.${prodDomain}`;
                })()
              } className="hover:text-white transition-colors duration-200">{t('footer_status') || 'System Status'}</a></li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-white tracking-wider uppercase text-sm font-sans">{t('footer_legal')}</h3>
            <ul className="mt-4 space-y-2 text-sm font-sans text-slate-300">
              <li><Link to="/privacy-policy" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_privacy')}</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_terms')}</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>{t('footer_cookies')}</Link></li>
              <li><Link to="/sitemap" className="hover:text-white transition-colors duration-200" onClick={handleLinkClick}>Sitemap</Link></li>
            </ul>
          </div>

        </div>

        {/* Bottom Area: Copyright & LanguageSwitcher */}
        <div className="mt-12 border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-sm text-center md:text-start font-sans text-slate-300">
            {getCopyrightText()}
          </p>

          {/* Language Switcher */}
          <div className="flex justify-center md:justify-start items-center gap-x-4">
            <Globe className="text-slate-400 dark:text-slate-300 w-5 h-5" />
            {languages.map((lang, index) => (
              <React.Fragment key={lang.code}>
                <button
                  onClick={() => setLanguage(lang.code)}
                  className={`text-sm transition-colors duration-200 font-sans ${language === lang.code
                    ? 'text-violet-400 dark:text-white font-semibold'
                    : 'text-slate-400 dark:text-slate-300 hover:text-violet-400 dark:hover:text-white'
                    }`}
                >
                  {lang.name}
                </button>
                {index < languages.length - 1 && <span className="text-slate-600 dark:text-slate-500">|</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

      </div>
    </footer>
  );
}
